import { cn } from "@/lib/utils";
import { formatKpiCurrency } from "@/lib/formats/currency";

export interface GoalProgressBarProps {
  goalAmount: number;
  paidAmount: number;
  invoicedAmount: number;
  className?: string;
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return formatKpiCurrency(value);
}

/** Show only when a commission goal exists and there is invoice activity to track. */
export function shouldShowCommissionProgressBar(
  goalAmount: number,
  paidAmount: number,
  invoicedAmount: number,
): boolean {
  return goalAmount > 0 && (invoicedAmount > 0 || paidAmount > 0);
}

export function GoalProgressBar({
  goalAmount,
  paidAmount,
  invoicedAmount,
  className,
}: GoalProgressBarProps) {
  if (!shouldShowCommissionProgressBar(goalAmount, paidAmount, invoicedAmount)) {
    return null;
  }

  const paidPct = Math.max(0, Math.min(100, (paidAmount / goalAmount) * 100));
  const invoicedPct = Math.max(0, Math.min(100, (invoicedAmount / goalAmount) * 100));
  const paidPctRounded = Math.round(paidPct);
  const invoicedPctRounded = Math.round(invoicedPct);
  const remainingPaid = Math.max(0, goalAmount - paidAmount);

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">Commission goal progress</p>
        <p className="text-sm text-muted-foreground">
          Remaining: <span className="font-semibold text-foreground">{formatCompact(remainingPaid)}</span>
        </p>
      </div>

      <div className="text-sm text-muted-foreground">
        <span className="text-foreground font-medium">{paidPctRounded}%</span> paid
        {" · "}
        <span className="text-foreground font-medium">{invoicedPctRounded}%</span> invoiced
      </div>

      <div
        className="relative h-3 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={paidPctRounded}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Commission goal progress: ${formatCompact(paidAmount)} paid of ${formatCompact(goalAmount)} (${paidPctRounded}%)`}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gray-900 dark:bg-gray-200 transition-all duration-500"
          style={{ width: `${invoicedPct}%` }}
        />
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-orange-500 transition-all duration-500"
          style={{ width: `${paidPct}%` }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-orange-500" />
          Paid: <span className="font-semibold text-foreground">{formatCompact(paidAmount)}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-gray-900 dark:bg-gray-200" />
          Invoiced: <span className="font-semibold text-foreground">{formatCompact(invoicedAmount)}</span>
        </span>
      </div>
    </div>
  );
}
